import React from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import classNames from 'classnames'

import ProfileAvatar from '../profile-avatar'
import Button from '../../../../components/button'
import deleteIcon from '../../../../../images/trash.svg'
import RoundIcon from '../../../../components/shared/settings/round-icon'
import PROFILE_FRAGMENT from '../../../../../graphql/fragments/profile.gql'
import styles from './profile-tile.css'

/* Renders a profile tile with avatar selection and optional controls (edit and delete).
 * onAvatarClick is passed for profile switching and omitted for profile management.
*/
function ProfileTile({
  profile,
  onEditProfile,
  onDeleteProfile,
  onAvatarClick,
  isEditActive,
  onActiveEdit,
  onDeactivateEdit,
  isProfileDisabled
}) {
  const isNewKidProfile = profile.isKid && profile.needToConfigure
  const isEditButtonShown =
    onEditProfile &&
    isEditActive &&
    !isProfileDisabled
  const isDeleteButtonShown =
    onDeleteProfile &&
    !profile.needToConfigure &&
    isEditActive &&
    !isProfileDisabled
  const setupButtonClass = profile.needToConfigure ? styles.setup : ''
  const avatarUri = isProfileDisabled
    ? profile.avatar.uriForDisabledProfile
    : profile.avatar.uri

  const editButton = (
    <Button
      secondary
      className={classNames(styles.editButton, setupButtonClass)}
      onClick={onEditProfile}
      text={isNewKidProfile ? 'Configure' : 'Edit'}
    />
  )

  const deleteButton = (
    <Button
      secondary
      className={styles.deleteButton}
      onClick={(event) => {
        event.stopPropagation()
        onDeleteProfile(event)
      }}
    >
      <RoundIcon alt="Delete Icon" src={deleteIcon} />
    </Button>
  )

  return (
    <div
      className={styles.wrapper}
      onMouseEnter={onActiveEdit}
      onMouseLeave={onDeactivateEdit}
      onClick={onActiveEdit}
    >
      {onAvatarClick
        ? (
          <Button
            secondary
            className={classNames(
              styles.avatar,
              styles.avatarClickable
            )}
            e2eIdentifier={`profile-selector-target:${profile.name}`}
            onClick={(event) => {
              onActiveEdit(event)
              onAvatarClick(event)
              event.stopPropagation()
            }}
          >
            <ProfileAvatar uri={avatarUri} />
          </Button>
        )
        : (
          <div
            className={classNames(styles.avatar,
              {
                [styles.hoverOverlay]: isEditButtonShown && isEditActive,
                [styles.hoverCircle]: !isEditButtonShown
              })}
          >
            <ProfileAvatar uri={avatarUri} />
          </div>
        )}
      <div className={styles.name}>{profile.name}</div>

      <div className={classNames(styles.controlWrapper, {
        [styles.controlWrapperCollapsed]: onAvatarClick
      })}
      >
        {isEditButtonShown && editButton}
        {isDeleteButtonShown && deleteButton}
      </div>
    </div>
  )
}

ProfileTile.propTypes = {
  profile: propType(PROFILE_FRAGMENT).isRequired,
  onEditProfile: PropTypes.func,
  onDeleteProfile: PropTypes.func,
  onAvatarClick: PropTypes.func,
  isEditActive: PropTypes.bool,
  onActiveEdit: PropTypes.func,
  onDeactivateEdit: PropTypes.func,
  isProfileDisabled: PropTypes.bool
}

ProfileTile.defaultProps = {
  onEditProfile: null,
  onDeleteProfile: null,
  onAvatarClick: null,
  isEditActive: false,
  isProfileDisabled: false,
  onActiveEdit: () => {
  },
  onDeactivateEdit: () => {
  }
}

export default ProfileTile
